import { useEffect, useMemo, useState } from 'react';
import { createReceipt, evaluateAction, type ActionReceipt, type Decision, type Mandate, type Profile, type ProposedAction } from './domain';
import {
  assets,
  attentionRules,
  creatorPipeline,
  mandates,
  modules,
  opportunities,
  profileContent,
  proposedActions,
  vaultItems,
  walletBudgets,
  wikiItems,
  type ViewId,
} from './demo-data';
import { CockpitSurface } from './CockpitSurface';
import { Companion } from './Companion';
import { ActionComposer, MandateEditor, ReceiptLedger } from './GovernanceWorkbench';
import { CommandPalette, SettingsPanel, SetupBanner } from './ProductControls';
import { APP_STATE_VERSION, usePersistentAppState, type AppState } from './persistence';

const formatMoney = (minor: number, currency = 'NOK') =>
  new Intl.NumberFormat('en', { style: 'currency', currency, maximumFractionDigits: 0 }).format(minor / 100);

const initialState: AppState = {
  version: APP_STATE_VERSION,
  ownerName: 'Human owner',
  onboardingComplete: false,
  profile: 'professional',
  activeView: 'overview',
  mandates,
  receipts: [],
  customActions: { personal: [], professional: [] },
  updatedAt: new Date().toISOString(),
};

function DecisionBadge({ decision }: { decision: Decision }) {
  return <span className="decision" data-decision={decision}>{decision.replace('_', ' ')}</span>;
}

function SectionHeader({ eyebrow, title, copy, action }: { eyebrow: string; title: string; copy: string; action?: string }) {
  return <div className="section-header">
    <div><p className="eyebrow">{eyebrow}</p><h2>{title}</h2><p>{copy}</p></div>
    {action && <button className="secondary-button" type="button">{action}</button>}
  </div>;
}

function Overview({ profile, mandate, onNavigate }: { profile: Profile; mandate: Mandate; onNavigate: (view: ViewId) => void }) {
  const evaluations = proposedActions[profile].map((action) => evaluateAction(action, mandate, new Date('2026-07-24T10:00:00.000Z')));
  const allowed = evaluations.filter((item) => item.decision === 'ALLOW').length;
  const stepUp = evaluations.filter((item) => item.decision === 'STEP_UP').length;
  const blocked = evaluations.filter((item) => ['DENY', 'HALT'].includes(item.decision)).length;

  return <>
    <section className="hero-card">
      <div className="hero-copy">
        <p className="eyebrow">HUMAN-OWNED PERSONAL AGENCY</p>
        <h1>Your life has an operating layer now.</h1>
        <p>One private system for memory, knowledge, media, money, assets, attention and governed action. The agent works continuously. The human remains the authority.</p>
        <div className="hero-actions">
          <button className="primary-button" type="button" onClick={() => onNavigate('cockpit')}>Open cockpit</button>
          <button className="secondary-button" type="button" onClick={() => onNavigate('governance')}>Inspect authority</button>
        </div>
      </div>
      <div className="twin-orbit" aria-label="Personal twin control chain">
        <div className="orbit-ring orbit-one" /><div className="orbit-ring orbit-two" />
        <div className="twin-core"><span>YOU</span><strong>Agency Core</strong><small>{profile}</small></div>
        <span className="orbit-node node-one">Speider</span><span className="orbit-node node-two">BARO</span><span className="orbit-node node-three">REHT</span><span className="orbit-node node-four">Receipts</span>
      </div>
    </section>

    <section className="metric-grid">
      <article><span>Knowledge objects</span><strong>3,184</strong><small>126 verified this week</small></article>
      <article><span>Recovered value</span><strong>{formatMoney(1840000)}</strong><small>Identified across time, IP and spend</small></article>
      <article><span>Agent budget</span><strong>{formatMoney(mandate.maxCumulativeSpendMinor)}</strong><small>{formatMoney(mandate.spentMinor)} used in current mandate</small></article>
      <article><span>Runtime outcomes</span><strong>{allowed} / {stepUp} / {blocked}</strong><small>Allow · step-up · blocked</small></article>
    </section>

    <section className="module-grid">
      {modules.filter((module) => module.id !== 'overview').map((module) =>
        <button className="module-card" type="button" key={module.id} onClick={() => onNavigate(module.id)}>
          <div><span className="module-index">0{modules.indexOf(module)}</span><span className="status-chip" data-status={module.status}>{module.status}</span></div>
          <h3>{module.label}</h3><p>{module.description}</p><b>Open module →</b>
        </button>)}
    </section>

    <section className="split-grid">
      <article className="surface-card"><SectionHeader eyebrow="TODAY" title="What deserves your attention" copy="BARO ranks opportunities by expected value, effort, risk and fit." />
        <div className="ranked-list">{opportunities.slice(0, 3).map((item, index) => <div key={item.title}><span className="rank">{index + 1}</span><div><strong>{item.title}</strong><small>{item.owner}</small></div><b>{item.value}</b></div>)}</div>
      </article>
      <article className="surface-card"><SectionHeader eyebrow="CONTROL" title="Nothing consequential crosses silently" copy="Every external action resolves against the active profile and mandate." />
        <div className="decision-summary">{proposedActions[profile].map((action, index) => { const clearance = evaluations[index]; return <div key={action.id}><div><strong>{action.purpose}</strong><small>{clearance.reasons.join(' · ')}</small></div><DecisionBadge decision={clearance.decision} /></div>; })}</div>
      </article>
    </section>
  </>;
}

function Vault() {
  return <section className="surface-card page-card"><SectionHeader eyebrow="PERSONAL VAULT" title="Your data has a home, a purpose and a boundary." copy="The vault stores identity, memory, credentials and permissions without turning access into authority." action="Add source" />
    <div className="data-table"><div className="table-head"><span>Category</span><span>Objects</span><span>State</span><span>Access</span></div>{vaultItems.map((item) => <div className="table-row" key={item.category}><strong>{item.category}</strong><span>{item.count}</span><span>{item.state}</span><span>{item.access}</span></div>)}</div>
    <div className="principle-banner"><strong>Access is not permission.</strong><span>Credentials are released only to a specific governed action, never to the agent as ambient power.</span></div>
  </section>;
}

function Wiki() {
  return <section className="surface-card page-card"><SectionHeader eyebrow="LIVING WIKI" title="A private, source-linked model of what you know." copy="The twin builds continuity across documents, conversations, decisions and lived experience." action="Capture knowledge" />
    <div className="wiki-grid">{wikiItems.map((item) => <article key={item.title}><div><span>{item.type}</span><b>{Math.round(item.confidence * 100)}% confidence</b></div><h3>{item.title}</h3><p>{item.sources} linked sources</p><small>Updated {item.updated}</small></article>)}</div>
    <div className="flow-line"><span>Reality</span><i>→</i><span>Record</span><i>→</i><span>Continuity</span><i>→</i><span>Admissible knowledge</span><i>→</i><span>Action evidence</span></div>
  </section>;
}

function Scout() {
  return <section className="surface-card page-card"><SectionHeader eyebrow="SPEIDER + BARO" title="Observe broadly. Decide narrowly." copy="Speider collects signals. BARO evaluates significance. Neither receives execution authority." action="Refresh signals" />
    <div className="opportunity-list">{opportunities.map((item, index) => <article key={item.title}><span className="opportunity-rank">#{index + 1}</span><div><h3>{item.title}</h3><p>Owner: {item.owner}</p></div><div className="score-set"><span>Value <b>{item.value}</b></span><span>Effort <b>{item.effort}</b></span><span>Risk <b>{item.risk}</b></span></div><button type="button">Prepare plan</button></article>)}</div>
  </section>;
}

function Creator() {
  return <section className="surface-card page-card"><SectionHeader eyebrow="CREATOR STUDIO" title="One idea. Every useful format." copy="Create video, music, podcasts, articles and social derivatives through provider-neutral production adapters." action="New production" />
    <div className="provider-strip"><span>Text + research</span><span>Higgsfield-like video</span><span>Suno-like music</span><span>Voice + podcast</span><span>Spotify</span><span>YouTube</span><span>Social channels</span></div>
    <div className="pipeline">{creatorPipeline.map((item) => <article key={item.title}><div className="format-mark">{item.format.slice(0, 2).toUpperCase()}</div><div><h3>{item.title}</h3><p>{item.format} · {item.channels.join(' · ')}</p></div><span className="stage" data-stage={item.stage}>{item.stage}</span></article>)}</div>
    <div className="principle-banner"><strong>Creation is not publication.</strong><span>Rights, claims, profile, channel and audience are re-evaluated immediately before release.</span></div>
  </section>;
}

function Assets() {
  return <section className="surface-card page-card"><SectionHeader eyebrow="ASSET MANAGER" title="Find value already inside your life." copy="Map underused knowledge, rights, subscriptions, relationships, time and physical assets." action="Scan assets" />
    <div className="asset-grid">{assets.map((asset) => <article key={asset.name}><span>{asset.kind}</span><h3>{asset.name}</h3><strong>{asset.estimatedMinor > 0 ? formatMoney(asset.estimatedMinor) : 'Strategic value'}</strong><p>{asset.status}</p><button type="button">{asset.next}</button></article>)}</div>
  </section>;
}

function Attention() {
  return <section className="surface-card page-card"><SectionHeader eyebrow="ATTENTION MARKET" title="Your attention is governed inventory." copy="Choose who may contact you, what they must offer, which data may be used and when compensation is required." action="New rule" />
    <div className="attention-list">{attentionRules.map((rule) => <article key={rule.source}><div><strong>{rule.source}</strong><p>{rule.condition}</p></div><span data-policy={rule.policy}>{rule.policy}</span></article>)}</div>
    <div className="offer-card"><div><p className="eyebrow">MACHINE-READABLE PREFERENCE</p><h3>Mortgage offers may reach me once per quarter only when total cost improves by at least 10%.</h3></div><code>purpose=refinance · frequency=quarterly · improvement≥10% · resale=false</code></div>
  </section>;
}

function Wallet({ profile, mandate }: { profile: Profile; mandate: Mandate }) {
  return <section className="surface-card page-card"><SectionHeader eyebrow="AGENT WALLET" title="Money with a mandate." copy="The wallet can buy compute, data, media, rights, distribution and expertise, but only inside a purpose-bound budget." action="Request budget" />
    <div className="wallet-hero"><div><span>Available balance</span><strong>{formatMoney(profileContent[profile].balanceMinor)}</strong><small>{profileContent[profile].name}</small></div><div><span>Delegated this period</span><strong>{formatMoney(mandate.maxCumulativeSpendMinor)}</strong><small>{formatMoney(mandate.spentMinor)} committed</small></div></div>
    <div className="budget-list">{walletBudgets.map((budget) => { const used = Math.round((budget.usedMinor / budget.limitMinor) * 100); return <article key={budget.category}><div><strong>{budget.category}</strong><span>{formatMoney(budget.usedMinor)} / {formatMoney(budget.limitMinor)}</span></div><div className="progress"><i style={{ width: `${used}%` }} /></div></article>; })}</div>
    <div className="spend-categories"><span>Compute</span><span>Data</span><span>Rights</span><span>Human expertise</span><span>Distribution</span><span>Other agents</span><span>Trust services</span></div>
  </section>;
}

function Governance({
  profile,
  mandate,
  receipts,
  actions,
  onEvaluate,
  onCompose,
  onMandateChange,
  onClearReceipts,
}: {
  profile: Profile;
  mandate: Mandate;
  receipts: ActionReceipt[];
  actions: ProposedAction[];
  onEvaluate: (action: ProposedAction) => void;
  onCompose: (action: ProposedAction) => void;
  onMandateChange: (next: Mandate) => void;
  onClearReceipts: () => void;
}) {
  return <>
    <section className="surface-card page-card"><SectionHeader eyebrow="RUNTIME GOVERNANCE" title="Capability is not authority." copy="Inspect each proposed action against principal, profile, purpose, budget, recipient, data scope, risk and evidence." />
      <div className="mandate-card"><div><span>Active mandate</span><strong>{mandate.id}</strong></div><div><span>Principal binding</span><strong>{mandate.principalId}</strong></div><div><span>Valid until</span><strong>{new Date(mandate.validUntil).toLocaleDateString()}</strong></div><div><span>Cumulative spend</span><strong>{formatMoney(mandate.spentMinor)} / {formatMoney(mandate.maxCumulativeSpendMinor)}</strong></div></div>
      <div className="action-stack">{actions.map((action) => { const clearance = evaluateAction(action, mandate, new Date()); return <article key={action.id}><div><span>{action.capability} · {action.risk} risk</span><h3>{action.purpose}</h3><p>{action.recipient ?? 'No external recipient'} · {formatMoney(action.amountMinor, action.currency)}</p><small>{clearance.reasons.join(' · ')}</small></div><div><DecisionBadge decision={clearance.decision} /><button type="button" onClick={() => onEvaluate(action)}>Issue receipt</button></div></article>; })}</div>
    </section>
    <div className="workbench-grid"><MandateEditor mandate={mandate} onChange={onMandateChange} /><ActionComposer profile={profile} mandate={mandate} onIssue={onCompose} /><ReceiptLedger receipts={receipts} onClear={onClearReceipts} /></div>
  </>;
}

export default function App() {
  const [state, setState] = usePersistentAppState(initialState);
  const [commandOpen, setCommandOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const activeModule = useMemo(() => modules.find((module) => module.id === state.activeView) ?? modules[0], [state.activeView]);
  const activeMandate = state.mandates[state.profile];
  const allActions = [...proposedActions[state.profile], ...state.customActions[state.profile]];
  const ownerInitials = state.ownerName.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase()).join('') || 'HO';

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') { event.preventDefault(); setCommandOpen(true); }
      if (event.key === 'Escape') { setCommandOpen(false); setSettingsOpen(false); }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const navigate = (activeView: ViewId) => setState((current) => ({ ...current, activeView }));
  const switchProfile = (profile: Profile) => setState((current) => ({ ...current, profile }));

  const evaluateAndRecord = (action: ProposedAction, remember = false) => {
    setState((current) => {
      const clearance = evaluateAction(action, current.mandates[current.profile]);
      const previousHash = current.receipts.at(0)?.receiptHash ?? 'GENESIS';
      const receipt = createReceipt(action, clearance, previousHash);
      return {
        ...current,
        receipts: [receipt, ...current.receipts],
        customActions: remember ? { ...current.customActions, [current.profile]: [action, ...current.customActions[current.profile]] } : current.customActions,
      };
    });
  };

  const renderView = () => {
    switch (state.activeView) {
      case 'cockpit': return <CockpitSurface />;
      case 'companion': return <Companion />;
      case 'vault': return <Vault />;
      case 'wiki': return <Wiki />;
      case 'scout': return <Scout />;
      case 'creator': return <Creator />;
      case 'assets': return <Assets />;
      case 'attention': return <Attention />;
      case 'wallet': return <Wallet profile={state.profile} mandate={activeMandate} />;
      case 'governance': return <Governance profile={state.profile} mandate={activeMandate} receipts={state.receipts} actions={allActions} onEvaluate={(action) => evaluateAndRecord(action)} onCompose={(action) => evaluateAndRecord(action, true)} onMandateChange={(next) => setState((current) => ({ ...current, mandates: { ...current.mandates, [current.profile]: next } }))} onClearReceipts={() => setState((current) => ({ ...current, receipts: [] }))} />;
      default: return <Overview profile={state.profile} mandate={activeMandate} onNavigate={navigate} />;
    }
  };

  return <div className="app-shell">
    <aside className="sidebar">
      <button className="brand" type="button" onClick={() => navigate('overview')}><span>P</span><div><strong>Personal Agency</strong><small>Operating System</small></div></button>
      <nav>{modules.map((module) => <button type="button" key={module.id} data-active={module.id === state.activeView} onClick={() => navigate(module.id)}><span>{module.shortLabel.slice(0, 2).toUpperCase()}</span><div><strong>{module.shortLabel}</strong><small>{module.status}</small></div></button>)}</nav>
      <div className="sidebar-principle"><span>HUMAN AUTHORITY</span><p>The system may prepare, recommend and execute only inside revocable mandate.</p><small>{navigator.onLine ? '● Online' : '○ Offline mode'}</small></div>
    </aside>
    <main className="main-shell">
      <header className="topbar">
        <div><p>{activeModule.label}</p><span>{activeModule.description}</span></div>
        <button className="global-search" type="button" onClick={() => setCommandOpen(true)}><span>⌕</span> Search across your OS… <kbd>⌘K</kbd></button>
        <div className="profile-switch"><button type="button" data-active={state.profile === 'personal'} onClick={() => switchProfile('personal')}>Personal</button><button type="button" data-active={state.profile === 'professional'} onClick={() => switchProfile('professional')}>Professional</button></div>
        <button className="settings-button" type="button" onClick={() => setSettingsOpen(true)} aria-label="Open settings">⚙</button>
        <button className="user-chip" type="button" onClick={() => setSettingsOpen(true)}><span>{ownerInitials}</span><div><strong>{state.ownerName}</strong><small>Authority active</small></div></button>
      </header>
      <div className="content-shell"><SetupBanner state={state} onChange={setState} onOpenSettings={() => setSettingsOpen(true)} />{renderView()}</div>
    </main>
    <CommandPalette open={commandOpen} onClose={() => setCommandOpen(false)} onNavigate={navigate} />
    <SettingsPanel open={settingsOpen} state={state} fallback={initialState} onClose={() => setSettingsOpen(false)} onChange={setState} />
  </div>;
}
